import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import { MAHARAJ_TIERS } from './maharajPricingData'

/** Laravel booleans can arrive as true/false, 1/0 or "1"/"0". */
const isFalse = (v) => v === false || v === 0 || v === '0' || v === 'false'

/**
 * The Maharaj priority tiers with LIVE prices/windows from
 * GET /astrologer/pricing-slabs. Response shape:
 *   { status, data: { astrologer_id, is_active,
 *     slabs: [{ label, min_days, max_days, price }, …] } }
 * Rows map onto the local tier config by ascending min_days (soonest window
 * = Zap); keys, labels and icons stay local. A failed read, missing rows, or
 * is_active=false falls back to the static MAHARAJ_TIERS so the booking flow
 * never breaks — the same list drives the tier cards, calendar day gating,
 * the fee and the payment amount, so they can never disagree.
 *
 * This page has no session (public, unauthenticated), so the Sanctum-guarded
 * "signed-in astrologer" resolution doesn't apply — `astrologerId` must be
 * passed explicitly (Raju Maharaj's resolved astrologer id) as a query param.
 */
export function useMaharajTiers(astrologerId) {
  const { data } = useQuery({
    queryKey: ['maharaj-pricing-slabs', astrologerId],
    queryFn: () => api.astrologers.pricingSlabs({ astrologer_id: astrologerId }),
    enabled: Boolean(astrologerId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  })

  return useMemo(() => {
    const rec = data && typeof data === 'object' && 'data' in data ? data.data : data
    const rows = Array.isArray(rec?.slabs)
      ? [...rec.slabs].sort((a, b) => (a?.min_days ?? 0) - (b?.min_days ?? 0))
      : []
    if (!rows.length || isFalse(rec?.is_active)) return MAHARAJ_TIERS
    return MAHARAJ_TIERS.map((tier, i) => {
      const row = rows[i]
      if (!row) return tier
      const price = Number(row.price)
      const minDays = Number(row.min_days)
      const maxDays = Number(row.max_days)
      return {
        ...tier,
        price: Number.isFinite(price) && price > 0 ? price : tier.price,
        minDays: Number.isFinite(minDays) ? minDays : tier.minDays,
        maxDays: Number.isFinite(maxDays) ? maxDays : tier.maxDays,
      }
    })
  }, [data])
}
